/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { BAR, PIE_GRADE } from 'src/components/Charts/types';
import { Card, SimpleChart } from '../../../components';
import { getQuestionTitlesByChartType } from '../../../components/Card/CardExporting/chartExporter/questionProcessor';
import { GRADE_MULTIPLA, GRADE_UNICA } from '../../../models/questionTypes';
import { annotationStrategyMap } from '../../../stores/actions/annotations';
import {
  fetchSimpleChartColorConfigData,
  fetchSimpleChartData
} from '../../../stores/actions/charts';

import { useConsolidations } from 'src/hooks/Data';
import SimpleChartThemeManager from 'src/managers/themeManager/simpleChartThemeManager';
import { isNullOrEmptyArray } from '../../../util/arrays';
import { dataToColors, getChartThemeColors } from '../../../util/colors';
import { removerCaracteresEspeciais } from '../../../util/formatters/stringNormalizer';
import ContextualCardAnnotations from '../../ContextualCardAnnotations';
import ContextualPopupExporting from '../../ContextualPopupExporting';
import { cardsMap } from '../cardsMap';
import { SIMPLE_CARD } from '../cardTypes';
import { exportingTypes } from '../exportingTypes';
import * as Ui from '../styles';
import { graphTypes } from '../types';
import { resolveChartType } from './chartTypeResolver';
import SimpleChartConsolidation, { CONSOLIDATION_SUCCESS } from './consolidation';
import { resetChartLegendsState } from 'src/managers/chartManager/stateManagement/legendHandler';

// mensagem para dados vazios
const msgEmptyChart = 'Selecione uma pesquisa e uma pergunta para ver os resultados';

// tipos de requisições que podem ocorrer, os dados plotados do gráfico podem ser recuperados através de duas requisições
// a default, que é através da seleção dos dados do dashboard (customer, survey e question) ou através de uma consolidação.
const DASHBOARD_REQUEST = 'dashboard';
const CONSOLIDATION_REQUEST = 'consolidation';

/**
 * filters a dataset using the data stored in the given
 * dataField that matches with the given keywords.
 */
// {dataset, datasetFilterField, query}
function filterByKeywords(context) {
  const { dataset, datasetFilterField, query } = context;
  if (!query) {
    return dataset;
  }
  if (query && query === '') {
    return dataset;
  }
  return dataset.filter((data) => removerCaracteresEspeciais(data[datasetFilterField])
    .toLowerCase()
    .includes(removerCaracteresEspeciais(query).toLowerCase()));
}

/**
 * @param data dados do grafico simples
 * @param isFullScreen flag indicando se o card do gráfico simples deve ser exibido
 * em fullScreen ou não.
 * @param isSomeoneInFullScreen flag indicando se existe algum card em full screen.
 * @param onFullScreenToggle callback chamado quando existe uma interação com o botão toggle de fullscreen.
 */
function SimpleChartCard(props) {
  const {
    resultRunning,
    resultError,
    resultPayload,
    consolidationData,
    dispatch,
  } = props;

  // props from parent
  const {
    surveyId,
    questionId,
    customerId,
    questionTitle,
    isFullScreen,
    isSomeoneInFullScreen,
    onFullScreenToggle,
    theme,
  } = props;
  // Retorna os dados de configuração do configCharts do redux (configuração de cores e fontes)

  // state
  const [data, setData] = useState([]);
  const [simpleChartType, setSimpleChartType] = useState('pie');
  const [isGradeQuestionType, setIsGradeQuestionType] = useState(false);
  const [tipoPergunta, setTipoPergunta] = useState('');
  const [filter, setFilter] = useState('');
  const [withData, setWithData] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [showExporting, setShowExporting] = useState(false);
  const [chartOrderIndex, setChartOrderIndex] = useState(0);
  const [renderChartInAscOrder, setRenderChartInAscOrder] = useState(false);
  // Estado para controlar a visualização da modal de consolidação
  const [showConsolidationModal, setShowConsolidationModal] = useState(false);
  //
  const [requestReason, setRequestReason] = useState(DASHBOARD_REQUEST);

  const chartRef = React.createRef();

  // Consolidações
  const consolidations = useConsolidations(data);

  // Gerenciador de tema de cores e fontes do gráfico simples
  const themeManager = new SimpleChartThemeManager(
    questionId,
    surveyId,
    withData,
    chartRef,
    theme,
    simpleChartType,
    SIMPLE_CARD,
    consolidations,
    renderChartInAscOrder
  );

  const {
    colorSchemeValue,
    fontsSizes,
    colorPickerColors,
    configurationError: simpleChartError,
  } = themeManager.getConfig();

  // handle full screen toggle
  function handleFullScreenToggle(cardId, isFull) {
    if (onFullScreenToggle) onFullScreenToggle(cardId, isFull);
  }

  function handleTypeChange(newType) {
    setSimpleChartType(newType);

    // Para perguntas de tipo grade, o gráfico de mini pizza deve ser iniciado com o card expandido.
    if (newType === PIE_GRADE) {
      handleFullScreenToggle(cardsMap.graficoSimples.cardId, true);
    }
  }

  // handle color scheme type change
  function handleColorSchemeChange(newType) {
    themeManager.updateColorScheme(newType);
  }

  // handle chart font type change
  function handleChartFontChange(newFontsSizes) {
    themeManager.updateFontSize(newFontsSizes);
  }

  // Altera a ordem da exibição dos dados no gráfico
  function handleSetDataOrder(newOrder) {
    setChartOrderIndex(newOrder)
    const revert = newOrder === 0 ? false : true;
    setRenderChartInAscOrder(revert);
  }

  /**
   * Effects: quando uma consolidação é configurada com sucesso é feita outra
   * requisição para recuperar os resultados gerais com as consolidações modificadas.
   */
  useEffect(() => {
    if (surveyId && questionId && !!consolidationData.status) {
      fetchSimpleChartData(surveyId, questionId, customerId, dispatch);
      setRequestReason(CONSOLIDATION_REQUEST);
      setShowConsolidationModal(false);
      toast.success(CONSOLIDATION_SUCCESS);
    }
  }, [dispatch, surveyId, questionId, customerId, consolidationData]);

  // quando ocorre erro
  useEffect(() => {
    if (resultError) {
      toast.error(resultError.toString());
    }
  }, [resultError]);

  useEffect(() => {
    if (!!consolidationData.error.length) {
      consolidationData.error.forEach((consolidationError) => {
        toast.error(consolidationError.toString());
      });
    }
  }, [consolidationData]);

  // quando ocorre erro no serviço de configurações
  useEffect(() => {
    if (simpleChartError) {
      toast.error(simpleChartError.toString());
    }
  }, [simpleChartError]);

  // quando os dados de pesquisa e da questão for definido, carregar as configuracoes dos graficos
  useEffect(() => {
    setWithData(false);
    resetChartLegendsState(SIMPLE_CARD);
    if (surveyId && questionId) {
      fetchSimpleChartData(surveyId, questionId, customerId, dispatch);
      setRequestReason(DASHBOARD_REQUEST);
      fetchSimpleChartColorConfigData(surveyId, questionId, dispatch);
    } else {
      setData([]);
    }
  }, [dispatch, surveyId, customerId, questionId]);

  // ao modificar o estado dos dados de plotagem
  useEffect(() => {
    // check if has data
    const hasData = resultPayload
      && resultPayload.perguntas
      && !isNullOrEmptyArray(resultPayload.perguntas);
    //
    if (hasData) {
      const resultados = resultPayload.perguntas[0].alternativas;

      const tipoPergunta = resultPayload.perguntas[0].tipo;

      if (tipoPergunta === GRADE_UNICA || tipoPergunta === GRADE_MULTIPLA) {
        setIsGradeQuestionType(true);
      } else if (isGradeQuestionType) {
        setIsGradeQuestionType(false);
      }

      setTipoPergunta(tipoPergunta);
      
      // Só queremos que o tipo de gráfico mude caso o motivo da requisição não seja uma consolidação de alternativas
      if (requestReason !== CONSOLIDATION_REQUEST) {
        const defaultType = resolveChartType(resultPayload.perguntas[0]);
        setSimpleChartType(tipoPergunta === 'MULTIPLA' ? BAR : defaultType);
      }

      setData(resultados);
      setWithData(true);
    } else {
      setData([]);
      setWithData(false);
      resetChartLegendsState(SIMPLE_CARD);
    }
  }, [resultPayload, requestReason]);

  function handleChangeFilterValue(text) {
    setFilter(text);
  }

  function handleCloseAnnotations() {
    setShowAnnotations(false);
  }

  /**
   * event: lida com a ação de fechar a modal de consolidação
   */
  function handleConsolidationClose() {
    setShowConsolidationModal(false);
  }

  function handleCloseExporting() {
    setShowExporting(false);
  }

  function handleAnnotationsCancel() {
    handleCloseAnnotations();
  }

  function handleExportingCancel() {
    handleCloseExporting();
  }

  function handleOpenAnnotations() {
    setShowAnnotations(true);
  }

  function handleOpenExporting() {
    setShowExporting(true);
  }

  function handleOpenConsolidationModal() {
    setShowConsolidationModal(true);
  }

  function handleAnnotationsSave() {
    setShowAnnotations(false);
  }

  function handleAnnotationsFailure(errorType) {
    const errorStrategy = {
      fetch: () => {
        handleCloseAnnotations();
        const message = 'Houve uma falha ao recuperar sua anotação, tente novamente';
        toast.error(message);
      },
      save: () => {
        const message = 'Não foi possível salvar sua anotação, tente novamente';
        toast.error(message);
      },
    };
    errorStrategy[errorType]();
  }

  // for render configurations
  // menu-structure: {type: [<charttypes>|<configbtn>]}
  // menu-structure: {charttypes = {type, key, selectedIndex, onSelect, data[{value, label}]}}
  // menu-structure: {configbtn = {type, key, onclick}}
  function buildMenu() {
    // result
    const result = [];
    // create chart menu

    // Key dinâmica para acesso aos tipos de gráficos que devem ser renderizados
    // no menu meatball de acordo com o tipo da pergunta atual.
    const chartTypeKey =
      (tipoPergunta === 'MULTIPLA' && 'multiple') ||
      (!isGradeQuestionType && 'type') || 'grade';

    const types = graphTypes.get('grafico-simples')[chartTypeKey];
    const selectedTypeIndex = types.findIndex(
      (type) => type.value === simpleChartType,
    );

    const colorSchemeTypes = graphTypes.get('grafico-simples').colorSchemeType;
    const selectedColorSchemeTypeIndex = colorSchemeTypes.findIndex(
      (type) => type.value === colorSchemeValue,
    );

    const chartFontTypes = graphTypes.get('grafico-simples').chartFontsType;
    const selectedFontTypeIndex = chartFontTypes.findIndex(
      (type) => type.value
        === (typeof fontsSizes === 'number' && !!fontsSizes ? fontsSizes : 14),
    );

    const chartOrderTypes = [
      { label: 'Maior para o menor', value: 0 },
      { label: 'Menor para o maior', value: 1 },
    ];
    const chartOrderTypesIndex = chartOrderTypes.findIndex(
      (type) => type.value === chartOrderIndex
    );

    const chartTypesOption = {
      type: 'charttypes',
      selectedIndex: selectedTypeIndex,
      data: types,
      onSelect: handleTypeChange,
    };

    const chartOrderOption = {
      type: 'chartorder',
      selectedIndex: chartOrderTypesIndex,
      data: chartOrderTypes,
      onSelect: handleSetDataOrder,
    };

    const colorSchemeOption = {
      type: 'colorscheme',
      selectedIndex: selectedColorSchemeTypeIndex,
      data: colorSchemeTypes,
      onSelect: handleColorSchemeChange,
    };

    const chartFontsOption = {
      type: 'chartfonts',
      selectedIndex: selectedFontTypeIndex,
      data: chartFontTypes,
      onSelect: handleChartFontChange,
    };

    const annotationsOption = {
      type: 'annotations',
      onClick: handleOpenAnnotations,
    };

    const exportChartOption = {
      type: 'exportChart',
      onClick: handleOpenExporting,
    };

    const consolidationOption = {
      title: 'Consolidação de alternativas...',
      type: 'settings',
      onClick: handleOpenConsolidationModal,
    };

    const options = data && data.length > 0 ? [
      chartTypesOption,
      annotationsOption,
      colorSchemeOption,
      chartFontsOption,
      exportChartOption,
      consolidationOption,
    ] : [
      chartTypesOption,
      colorSchemeOption,
      chartFontsOption,
    ];

    // Se o grafico for de barras adicionada o filtro de ordenação
    if(simpleChartType === BAR) {
      options.splice(2, 0, chartOrderOption)
    }

    result.push(...options.map((option, index) => ({ ...option, key: `#key${index}` })));

    return result;
  }

  function renderAnnotations() {
    return showAnnotations ? (
      <ContextualCardAnnotations
        strategy={annotationStrategyMap.simpleResults}
        customerId={customerId}
        surveyId={surveyId}
        resultKey={questionId}
        onCancel={handleAnnotationsCancel}
        onSave={handleAnnotationsSave}
        onFailure={handleAnnotationsFailure}
      />
    ) : null;
  }

  function renderExporting() {
    return showExporting && (
      <ContextualPopupExporting
        onCancel={handleExportingCancel}
        types={exportingTypes}
        selectedChartType={simpleChartType}
        selectedCardType={SIMPLE_CARD}
        theme={theme}
        questionLabels={getQuestionTitlesByChartType(SIMPLE_CARD, questionTitle)}
        surveyId={surveyId}
        analyzeId={questionId}
        customerId={customerId}
      />
    );
  }

  /**
   * renderiza o componente de consolidação
   */
  function renderConsolidationModal() {
    return showConsolidationModal ? (
      <SimpleChartConsolidation
        onClose={handleConsolidationClose}
        surveyId={surveyId}
        questionId={questionId}
        customerId={customerId}
        fromServerConsolidations={consolidations}
        questionTitle={questionTitle}
        isOpen
        theme={theme}
        isLoading={consolidationData.running}
      />
    ) : null;
  }

  return (
    <Ui.CardContainer
      className="cardContainer"
      cardIsFullScreen={isFullScreen}
      someoneInFullScreen={isSomeoneInFullScreen}
    >
      <Card
        withPresets={false}
        questionTitle={questionTitle}
        cardTitle="Gráfico Simples"
        cardTooltip={'Confira a distribuição das respostas da pergunta selecionada'}
        cardId={cardsMap.graficoSimples.cardId}
        contentHeight={'300px'}
        isFullScreenMode={isFullScreen}
        menu={buildMenu()}
        withData={withData}
        emptyChartText={msgEmptyChart}
        loading={resultRunning}
        onMaximize={handleFullScreenToggle}
        onChangeFilterValue={handleChangeFilterValue}
        theme={theme}
        isGradeQuestionType={isGradeQuestionType}
      >
        <SimpleChart
          chartFontSize={
            typeof fontsSizes === 'number' && !!fontsSizes ? fontsSizes : 14
          }
          type={simpleChartType}
          data={data}
          tipoPergunta={tipoPergunta}
          legendsFilter={filter}
          legendsFilterHandler={filterByKeywords}
          theme={theme.colors.charts}
          themeTitle={theme.title}
          colors={themeManager.calculatedChartColorArray}
          updateChartColors={themeManager.updateChartColors}
          colorPickerColors={colorPickerColors}
          error={simpleChartError}
          chartRef={chartRef}
          ascOrder={renderChartInAscOrder}
        />
      </Card>
      {renderConsolidationModal()}
      {renderAnnotations()}
      {renderExporting()}
    </Ui.CardContainer>
  );
}

const mapStateToProps = (state) => {

  const {
    simpleChartData: resultState,
  } = state.charts;

  const {
    simple: consolidationData,
  } = state.consolidation;

  return {
    resultRunning: resultState.running,
    resultError: resultState.error,
    resultPayload: resultState.payload,
    consolidationData,
  };
};

export default connect(mapStateToProps)(SimpleChartCard);
